import { FC, useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { strToBase64 } from '@utils/convertString';
import { useDebounce } from '@hooks/useDebounce';
import { SelectComponent } from '@inputs/SelectComponent/SelectComponent';
import { Obj } from '@/types/common';
import { AutocompleteSelectProps } from './types';

export function AutocompleteSelect<T extends Obj>({
  data,
  onSearch,
  afterSelect,
  base64Converting = false,
  filterArray,
  name,
  onSelect,
  onMultiSelect,
  ...selectProps
}: AutocompleteSelectProps<T>) {
  const { setValue } = useFormContext();

  const [options, setOptions] = useState<T[]>([]);

  useEffect(() => {
    setOptions(data || []);
  }, [data]);

  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const debouncedVaue = useDebounce(searchValue);

  useEffect(() => {
    search(debouncedVaue);
  }, [debouncedVaue]);

  const search = useCallback(
    async (search: string) => {
      if (search) {
        if (onSearch) {
          setIsLoading(true);
          let searched = await onSearch(base64Converting ? strToBase64(search) : search);
          if (searched) {
            if (filterArray) {
              const filtred = searched?.filter(
                ({ id }) => !filterArray.some((item) => item.id === id)
              );
              setOptions(filtred);
            } else setOptions(searched);
          }

          setIsLoading(false);
        } else {
          const filtred = data?.filter((item) =>
            item.name.toLowerCase().includes(search.toLowerCase())
          );
          setOptions(filtred || []);
        }
      } else {
        // set defalul options or nothing
        setOptions(data || []);
      }
    },
    [data, onSearch]
  );

  const handleSelect = useCallback(
    (val: T) => {
      // save selected value with some presetting
      if (onSelect) onSelect(val);
      setValue(name, val, { shouldValidate: true, shouldDirty: true });
      if (afterSelect) afterSelect();
    },
    [onSelect, afterSelect]
  );

  const handleMultiSelect = useCallback(
    (val: T[]) => {
      // save selected value with some presetting
      if (onMultiSelect) {
        onMultiSelect(val);
        return;
      }
      setValue(name, val);
      if (afterSelect) afterSelect();
    },
    [onMultiSelect, afterSelect]
  );

  return (
    <SelectComponent
      {...selectProps}
      name={name}
      options={options as T[]}
      onSelect={handleSelect}
      onMultiSelect={handleMultiSelect}
      onInputChange={setSearchValue}
      isLoading={isLoading}
      isSearchSelect
    />
  );
}
